import * as Yup from "yup";
import i18next from "../i18n";
import { DELIVERY_TYPE, ORDER_TYPES } from "../types/enums";

const regexPassword =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@\-#.`~_:;"|($)[\]{}%^&?¿*<>+=])(?!.*\s).{10,30}$/;

export const registerValidationSchema = Yup.object().shape({
  companyName: Yup.string().required(i18next.t("validations.companyName")),
  commercialName: Yup.string().required(
    i18next.t("validations.commercialName")
  ),
  firstName: Yup.string().required(i18next.t("validations.firstName")),
  lastName: Yup.string().required(i18next.t("validations.lastName")),
  phone: Yup.string().required(i18next.t("validations.phone")),
  taxId: Yup.string().required(i18next.t("validations.taxId")),
  country: Yup.string().required(i18next.t("validations.country")),
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .required(i18next.t("validations.email")),
  confirmEmail: Yup.string().oneOf(
    [Yup.ref("email"), undefined],
    i18next.t("validations.confirmEmail")
  ),
  password: Yup.string()
    .matches(regexPassword, i18next.t("validations.passwordTerms"))
    .min(12, i18next.t("validations.characters"))
    .required(i18next.t("validations.password")),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref("password"), undefined],
    i18next.t("validations.confirmPassword")
  ),
  age: Yup.boolean().oneOf([true], i18next.t("validations.age")),
  termsAndConditions: Yup.boolean().oneOf(
    [true],
    i18next.t("validations.termsAndConditions")
  ),
  informationAgreement: Yup.boolean().notRequired(),
  invitationCode: Yup.string().notRequired(),
});

export const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .max(255)
    .required(i18next.t("validations.email")),
  password: Yup.string().required(i18next.t("validations.password")),
});

export const resetPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .max(255)
    .required(i18next.t("validations.email")),
});

// Factory, not a static schema (Task 5, Profile/Company flow): the backend's `ChangePassword`
// DTO now requires a verified `currentPassword` UNLESS the caller is mid RESET_PASSWORD flow
// (AuthController#changePassword's `resetFlow` branch — a token-reset caller has no current
// password to supply). `Profile.tsx` is the only consumer (grepped), so this widening can't
// break another form. `requireCurrentPassword` mirrors that same branch client-side: normal
// mode passes `true` (field required, blocks submission before it ever reaches the server),
// reset mode passes `false` (field omitted from the form entirely, so nothing to validate).
export const changePasswordSchema = (requireCurrentPassword: boolean) =>
  Yup.object().shape({
    currentPassword: requireCurrentPassword
      ? Yup.string().required(i18next.t("validations.currentPassword"))
      : Yup.string().notRequired(),
    password: Yup.string()
      .matches(regexPassword, i18next.t("validations.passwordTerms"))
      .min(12, i18next.t("validations.characters"))
      .required(i18next.t("validations.newPassword")),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("password"), undefined],
      i18next.t("validations.confirmPassword")
    ),
  });

export const newPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .max(255)
    .required(i18next.t("validations.email")),
  password: Yup.string()
    .matches(regexPassword, i18next.t("validations.passwordTerms"))
    .min(12, i18next.t("validations.characters"))
    .required(i18next.t("validations.password")),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref("password"), undefined],
    i18next.t("validations.confirmPassword")
  ),
});

export const newMaterialValidationSchema = Yup.object().shape({
  ean: Yup.string().required(i18next.t("validations.ean")),
  globalMaterialName: Yup.string().required(
    i18next.t("validations.globalMaterialName")
  ),
  measurementUnit: Yup.string().required(
    i18next.t("validations.selectMeasurementUnit")
  ),
  customMeasurementUnit: Yup.string().when("measurementUnit", {
    is: i18next.t("measurementUnits.other"),
    then: (schema: any) =>
      schema.required(i18next.t("validations.customMeasurementUnit")),
    otherwise: (schema: any) => schema.notRequired(),
  }),
  deliveryPlace: Yup.string().required(
    i18next.t("validations.selectDeliveryPlace")
  ),
  details: Yup.string().max(255, i18next.t("validations.details255")),
  deliveryFormat: Yup.string(),
  specifications: Yup.string(),
});

// `editMaterialValidationSchema` used to live here too. It was extracted into
// `components/Form/editMaterialFormValidationSchema.ts` (Task 7, Materials edit flow — the same
// move `editSiteValidationSchema` made for Sites) and deleted from this file entirely: grepping
// found no importer besides the pre-Carbon `EditMaterialForm.tsx`, which that task rewrote to
// import the new module's `editMaterialValidationSchema` (now reusing Task 6's
// `newMaterialGoodsValidationSchema` directly) instead.

export const newServiceValidationSchema = Yup.object().shape({
  ean: Yup.string().required(i18next.t("validations.ean")),
  globalMaterialName: Yup.string().required(
    i18next.t("validations.globalMaterialName")
  ),
});

// `editSiteValidationSchema` used to live here too. It was extracted into
// `components/Form/editSiteValidationSchema.ts` (Task 6, Sites flow) and deleted from this file
// entirely — grepping found no importer besides `EditSiteForm.tsx`, which that task rewrote.
//
// `newSiteValidationSchema` stays: `containers/OrderByRequest/InlineCreateSiteModal.tsx` still
// imports it for its own unmigrated, hand-rolled `register`/`InputField` form (see that
// component and `components/Form/newSiteValidationSchema.ts`'s docstring for why it wasn't
// touched). `NewSiteForm.tsx` itself now uses the co-located module instead.
export const newSiteValidationSchema = Yup.object().shape({
  name: Yup.string().required(i18next.t("validations.siteName")),
  internalCode: Yup.string().required(i18next.t("validations.internalCode")),
  country: Yup.string().required(i18next.t("validations.country")),
});

export const biddingValidationSchema = Yup.object().shape({
  quantity: Yup.number()
    .typeError(i18next.t("validations.globalMaterialName"))
    .min(1, i18next.t("validations.tender.volume"))
    .required(i18next.t("validations.validVolume")),
  startSupplyDate: Yup.string().required(
    i18next.t("validations.tender.period")
  ),
  endSupplyDate: Yup.string().required(i18next.t("validations.tender.period")),
  startDate: Yup.string().required(i18next.t("validations.tender.start")),
  finishDate: Yup.string().required(i18next.t("validations.tender.end")),
  paymentMethod: Yup.string().required(
    i18next.t("validations.tender.paymentMethod")
  ),
  paymentTerm: Yup.string().required(
    i18next.t("validations.tender.paymentTerm")
  ),
});

export const newOfferValidationSchema = (type?: ORDER_TYPES, max?: number) => {
  if (type === ORDER_TYPES.INVERSE || type === ORDER_TYPES.INVERSE_JOINT) {
    return Yup.object().shape({
      price: Yup.number()
        .transform((value: any, originalValue: any) => {
          if (typeof originalValue === "string") {
            const parsedValue = originalValue
              .replace(/\./g, "")
              .replace(",", ".")
              .replace("$ ", "");
            return parsedValue ? parseFloat(parsedValue) : NaN;
          }
          return value;
        })
        .min(0.00000001, i18next.t("validations.moreThanZero"))
        .max(max, `${i18next.t("validations.lowerThanX")} ${max}`)
        .required(i18next.t("validations.insertPrice")),
    });
  }
  return Yup.object().shape({
    price: Yup.number()
      .transform((value: any, originalValue: any) => {
        if (typeof originalValue === "string") {
          const parsedValue = originalValue
            .replace(/\./g, "")
            .replace(",", ".")
            .replace("$ ", "");
          return parsedValue ? parseFloat(parsedValue) : NaN;
        }
        return value;
      })
      .min(1, i18next.t("validations.moreThanZero"))
      .required(i18next.t("validations.insertPrice")),
  });
};

export const newMaterialOfferValidationSchema = Yup.object().shape({
  origin: Yup.string().required(i18next.t("validations.origin")),
  comment: Yup.string().max(255, i18next.t("validations.comment255")),
  specificationFileUrl: Yup.string(),
});

export const purchaseRequestOrderValidationSchema = Yup.object().shape({
  name: Yup.string().required(i18next.t("validations.name")),
  spot: Yup.string().required(),
  file: Yup.mixed().required(
    i18next.t("purchaseRequestOrders.validations.file")
  ),
  startSupplyDate: Yup.date()
    .nullable()
    .transform((value: string, originalValue: string) =>
      originalValue === "" ? null : value
    )
    .required(i18next.t("purchaseRequestOrders.validations.startSupplyDate")),
  endSupplyDate: Yup.date()
    .nullable()
    .transform((value: string, originalValue: string) =>
      originalValue === "" ? null : value
    )
    .when("spot", {
      is: DELIVERY_TYPE.SPOT,
      then: (schema: any) => schema.nullable().notRequired(),
      otherwise: (schema: any) =>
        schema
          .min(
            Yup.ref("startSupplyDate"),
            i18next.t("purchaseRequestOrders.validations.endSupplyDateMin")
          )
          .required(
            i18next.t("purchaseRequestOrders.validations.endSupplyDate")
          ),
    }),
});

// The two delivery schemas were extracted to the create-delivery form's own folder during the
// Carbon migration (Deliveries flow, Task 6) so the schema and the form's field shape live
// together. Re-exported here to keep this module's single import path — `deliveryValidations.test.ts`
// and any other consumer still import them from `utils/validations`.
export {
  deliveryValidationSchema,
  manualDeliveryValidationSchema,
} from "../components/Form/NewDelivery/newDeliveryValidationSchema";

export const newSupplierValidationSchema = Yup.object().shape({
  companyName: Yup.string().required(i18next.t("validations.companyName")),
  cuit: Yup.string().required(i18next.t("validations.taxId")),
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .required(i18next.t("validations.email")),
  firstName: Yup.string(),
  lastName: Yup.string(),
});

export const registrationCompletionValidationSchema = Yup.object().shape({
  // Company data
  companyName: Yup.string().required(i18next.t("validations.companyName")),
  commercialName: Yup.string().required(
    i18next.t("validations.commercialName")
  ),
  taxId: Yup.string().required(i18next.t("validations.taxId")),
  country: Yup.string().required(i18next.t("validations.country")),
  // User data
  firstName: Yup.string().required(i18next.t("validations.firstName")),
  lastName: Yup.string().required(i18next.t("validations.lastName")),
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .required(i18next.t("validations.email")),
  phone: Yup.string().required(i18next.t("validations.phoneNumber")),
  // Password
  password: Yup.string()
    .matches(regexPassword, i18next.t("validations.passwordTerms"))
    .min(12, i18next.t("validations.characters"))
    .required(i18next.t("validations.password")),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref("password"), undefined],
    i18next.t("validations.confirmPassword")
  ),
});

export const companyProfileValidationSchema = Yup.object().shape({
  // Basic Information - all optional
  website: Yup.string()
    .url(i18next.t("validations.validUrl"))
    .nullable()
    .optional(),
  linkedinUrl: Yup.string()
    .url(i18next.t("validations.validUrl"))
    .nullable()
    .optional(),
  annualRevenue: Yup.number()
    .typeError(i18next.t("validations.validNumber"))
    .min(0, i18next.t("validations.positiveNumber"))
    .nullable()
    .optional(),
  taxDocuments: Yup.string().nullable().optional(),

  // Addresses - all optional
  addresses: Yup.array()
    .of(
      Yup.object().shape({
        street: Yup.string().nullable().optional(),
        city: Yup.string().nullable().optional(),
        state: Yup.string().nullable().optional(),
        postalCode: Yup.string().nullable().optional(),
        countryId: Yup.string().nullable().optional(),
        isLegalAddress: Yup.boolean().default(false),
        isDeliverySite: Yup.boolean().default(false),
      })
    )
    .nullable()
    .optional(),

  // Contacts - all optional
  contacts: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().nullable().optional(),
        email: Yup.string()
          .email(i18next.t("validations.validEmail"))
          .nullable()
          .optional(),
        phone: Yup.string().nullable().optional(),
        position: Yup.string().nullable().optional(),
        type: Yup.string()
          .oneOf(["FINANCIAL", "QUALITY", "LEGAL_REPRESENTATIVE"])
          .nullable()
          .optional(),
      })
    )
    .nullable()
    .optional(),

  // Certifications - all optional
  certifications: Yup.array()
    .of(
      Yup.object().shape({
        companyCertificationId: Yup.string().nullable().optional(),
        valid: Yup.boolean().default(false),
        fileURL: Yup.string().nullable().optional(),
      })
    )
    .nullable()
    .optional(),

  // Main Clients - all optional
  mainClients: Yup.array()
    .of(
      Yup.object().shape({
        clientName: Yup.string().nullable().optional(),
        description: Yup.string().nullable().optional(),
      })
    )
    .nullable()
    .optional(),

  // Files - all optional
  files: Yup.array()
    .of(
      Yup.object().shape({
        fileURL: Yup.string().nullable().optional(),
        name: Yup.string().nullable().optional(),
        description: Yup.string().nullable().optional(),
        type: Yup.string()
          .oneOf(["PRODUCT_CATALOG", "COMPANY_PRESENTATION"])
          .nullable()
          .optional(),
      })
    )
    .nullable()
    .optional(),

  // Banking Information - all optional
  bankingInfo: Yup.object()
    .shape({
      bankName: Yup.string().nullable().optional(),
      accountNumber: Yup.string().nullable().optional(),
      accountType: Yup.string().nullable().optional(),
      swift: Yup.string().nullable().optional(),
      iban: Yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
